import React, { useState, useRef, useEffect } from 'react';
import { useImagePreloader } from '../hooks/useImagePreloader';
import BlurhashPlaceholder from './BlurhashPlaceholder';
import '../styles/OptimizedImage.css';

/**
 * Optimized Image Component with progressive loading, lazy loading, and blurhash placeholders
 */
const OptimizedImage = ({
  src,
  alt = '',
  className = '',
  placeholder = null,
  blurhash = null,
  priority = false,
  lazy = true,
  onLoad = () => {},
  onError = () => {},
  style = {},
  ...props
}) => {
  const [isIntersecting, setIsIntersecting] = useState(!lazy);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);
  const observerRef = useRef(null);

  // Check if blurhash is valid
  const hasBlurhash = blurhash && typeof blurhash === 'string' && blurhash.trim().length > 0;

  // Only start loading when image is in viewport (or priority/not lazy)
  const shouldLoad = priority || !lazy || isIntersecting;
  const { isLoaded, isLoading, error } = useImagePreloader(
    shouldLoad ? src : null,
    { priority, lazy: !shouldLoad }
  );

  // Intersection Observer for lazy loading with aggressive preloading
  useEffect(() => {
    if (!lazy || priority) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsIntersecting(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.01,
        rootMargin: '400px' // Start loading 400px before image enters viewport
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [lazy, priority]);

  // Handle errors
  useEffect(() => {
    if (error) {
      setHasError(true);
      onError(error);
    }
  }, [error, onError]);

  // Handle successful load
  useEffect(() => {
    if (isLoaded && !error) {
      onLoad();
    }
  }, [isLoaded, error, onLoad]);

  // Error state
  if (hasError) {
    return (
      <div 
        ref={imgRef}
        className={`optimized-image-error ${className}`}
        style={{
          ...style,
          backgroundColor: '#f5f5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#999',
          fontSize: '12px',
        }}
        {...props}
      >
        {alt || 'Image failed to load'}
      </div>
    );
  }

  // Loading state with blurhash placeholder
  if (!shouldLoad || !isLoaded) {
    return (
      <div 
        ref={imgRef}
        className={`optimized-image-placeholder ${className} ${hasBlurhash ? 'optimized-image-placeholder--blurhash' : ''}`}
        style={{
          ...style,
          backgroundColor: '#e9ebe8',
          position: 'relative',
          overflow: 'hidden',
        }}
        {...props}
      >
        {/* Render blurhash canvas if available */}
        {hasBlurhash && (
          <BlurhashPlaceholder hash={blurhash} />
        )}
        
        {/* Shimmer loading effect - only show if no blurhash */}
        {isLoading && !hasBlurhash && (
          <div
            className="optimized-image-shimmer"
            style={{
              position: 'absolute',
              top: 0,
              left: '-100%',
              width: '100%',
              height: '100%',
              background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
              animation: 'shimmer 1.5s infinite',
            }}
          />
        )}
        
        {placeholder && typeof placeholder !== 'string' ? placeholder : null}
      </div>
    );
  }

  // Loaded state - show actual image
  return (
    <img
      ref={imgRef}
      src={src}
      alt={alt}
      className={`optimized-image ${className}`}
      style={{
        ...style,
        transition: 'opacity 0.3s ease-out',
      }}
      loading={lazy && !priority ? 'lazy' : 'eager'}
      {...props}
    />
  );
};

export default OptimizedImage;
